"use client";
import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import NextLink from "next/link";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import { useTranslation } from "react-i18next";

const arrowSx = {
  fontSize: 18,
  color: "#D9B3EF",
  stroke: "#D9B3EF",
  strokeWidth: 1,
};
const StepCard = ({ number, title, variant = "default", isArabic }) => {
  const styles = {
    default: {
      bg: "#FFFFFF",
      border: "1px solid #EDE6FA",
      numColor: "#8A88A3",
      titleColor: "#14122B",
    },
    purple: {
      bg: "#A020E8",
      border: "1px solid #A020E8",
      numColor: "rgba(255,255,255,0.75)",
      titleColor: "#FFFFFF",
    },
    dark: {
      bg: "#14122B",
      border: "1px solid #14122B",
      numColor: "rgba(255,255,255,0.65)",
      titleColor: "#FFFFFF",
    },
  }[variant];

  return (
    <Box
      sx={{
        backgroundColor: styles.bg,
        border: styles.border,
        borderRadius: "12px",
        px: { xs: 2.5, md: 3 },
        py: { xs: 2, md: 2.5 },
        textAlign: isArabic ? "right" : "left",
        boxShadow: "0 2px 12px rgba(122,43,245,0.04)",
      }}
    >
      <Typography
        sx={{
          fontSize: "10px",
          fontWeight: 500,
          letterSpacing: "1px",
          color: styles.numColor,
          fontFamily: "var(--font-outfit)",
          mb: 0.5,
        }}
      >
        {number}
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: "15px", md: "16px" },
          fontWeight: 700,
          color: styles.titleColor,
          fontFamily: "var(--font-plusjakarta)",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

const ScoutHowItWorksSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const raw = t("scoutHowItWorks.steps", { returnObjects: true });
  const steps = Array.isArray(raw) ? raw : [];
  const num = (i) => `0${i + 1}`;
  const variantFor = (i) => (i === 3 ? "purple" : i === 4 ? "dark" : "default");

  // Mobile order matches the desktop flow: 04 = Pipeline fills, 05 = Outreach sent
  // (text + colors stay with their card, only the position/number changes)
  const mobileOrder =
    steps.length >= 5 ? [0, 1, 2, 4, 3] : steps.map((_, i) => i);

  // arrows flip automatically in RTL
  const HorizontalArrow = () => (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <ArrowForwardRoundedIcon
        sx={{ ...arrowSx, transform: isArabic ? "scaleX(-1)" : "none" }}
      />
    </Box>
  );
  const ReverseArrow = () => (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <ArrowBackRoundedIcon
        sx={{ ...arrowSx, transform: isArabic ? "scaleX(-1)" : "none" }}
      />
    </Box>
  );

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        backgroundColor: "#fff",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography
            sx={{
              fontSize: { xs: "10px", sm: "11px" },
              fontWeight: 600,
              color: "#A020E8",
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              fontFamily: "var(--font-outfit)",
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            {t("scoutHowItWorks.label")}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "26px", sm: "30px", md: "34px", lg: "38px" },
              fontWeight: 700,
              lineHeight: 1.25,
              color: "#14122B",
              fontFamily: "var(--font-plusjakarta)",
              mb: { xs: 2, sm: 2.5 },
            }}
          >
            {t("scoutHowItWorks.heading")}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "14px", sm: "15px", md: "16px" },
              color: "#55556E",
              lineHeight: 1.8,
              fontFamily: "var(--font-outfit)",
              maxWidth: 720,
              mx: "auto",
            }}
          >
            {t("scoutHowItWorks.description")}
          </Typography>
        </Box>

        {/* Desktop flow */}
        {steps.length >= 5 && (
          <Box
            sx={{
              display: { xs: "none", md: "grid" },
              gridTemplateColumns: "1fr 40px 1fr 40px 1fr",
              alignItems: "center",
              rowGap: 1.5,
              maxWidth: 900,
              mx: "auto",
              mb: 6,
            }}
          >
            {/* Row 1 */}
            <StepCard number={num(0)} title={steps[0]} isArabic={isArabic} />
            <HorizontalArrow />
            <StepCard number={num(1)} title={steps[1]} isArabic={isArabic} />
            <HorizontalArrow />
            <StepCard number={num(2)} title={steps[2]} isArabic={isArabic} />

            {/* Row 2 - down arrow under step 3 */}
            <Box sx={{ gridColumn: "5 / 6", display: "flex", justifyContent: "center" }}>
              <ArrowDownwardRoundedIcon sx={arrowSx} />
            </Box>

            {/* Row 3 - reversed. Text/colors keep their position; only numbers are swapped:
                left (purple) = 05, right (dark) = 04 */}
            <Box sx={{ gridColumn: "3 / 4", gridRow: 3 }}>
              <StepCard number={num(4)} title={steps[3]} variant="purple" isArabic={isArabic} />
            </Box>
            <Box sx={{ gridColumn: "4 / 5", gridRow: 3 }}>
              <ReverseArrow />
            </Box>
            <Box sx={{ gridColumn: "5 / 6", gridRow: 3 }}>
              <StepCard number={num(3)} title={steps[4]} variant="dark" isArabic={isArabic} />
            </Box>
          </Box>
        )}

        {/* Mobile flow */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
            alignItems: "stretch",
            gap: 1,
            mb: 4,
          }}
        >
          {mobileOrder.map((stepIndex, i) => (
            <React.Fragment key={stepIndex}>
              <StepCard
                number={num(i)}
                title={steps[stepIndex]}
                variant={variantFor(stepIndex)}
                isArabic={isArabic}
              />
              {i < mobileOrder.length - 1 && (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <ArrowDownwardRoundedIcon sx={arrowSx} />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>

        {/* CTA */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <NextLink href="/free-trial-of-ai-recruiting-tool" passHref legacyBehavior>
            <Button
              component="a"
              sx={{
                bgcolor: "#000",
                color: "white",
                px: { xs: 3, sm: 3.5 },
                py: { xs: 1.2, sm: 1.4 },
                borderRadius: "50px",
                textTransform: "none",
                fontSize: { xs: "14px", sm: "15px" },
                fontWeight: 500,
                fontFamily: "var(--font-outfit)",
                whiteSpace: "nowrap",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: "#A020E8",
                  transform: "scale(1.03)",
                },
              }}
            >
              {t("scoutHowItWorks.cta")}
            </Button>
          </NextLink>
        </Box>
      </Container>
    </Box>
  );
};

export default ScoutHowItWorksSection;